import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, BadgeDollarSign, Phone, Plane, Check, CalendarDays, MapPin } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import api from "@/lib/api";
import CarCard from "@/components/CarCard";
import HeroSearch from "@/components/HeroSearch";
import { Seo } from "@/components/Seo";

const HERO_BG = "https://images.unsplash.com/flagged/photo-1579782647395-2e6fb36a64f2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920&auto=format&fit=crop";
const AIRPORT = "https://images.unsplash.com/photo-1750259051416-358589f5409c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxCb3N0b24lMjBMb2dhbiUyMGFpcnBvcnQlMjB0ZXJtaW5hbHxlbnwwfHx8fDE3ODI1OTUyNzJ8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format";

const FILTER_TABS = [
  { label: "All cars", to: "/fleet", testid: "all-cars" },
  { label: "Logan Airport", to: "/logan-airport-car-rental", testid: "logan-airport" },
  { label: "Weekly", to: "/boston-weekly-car-rental", testid: "weekly" },
  { label: "Monthly", to: "/long-term-car-rental-boston", testid: "monthly" },
  { label: "Economy", to: "/economy-car-rental-boston", testid: "economy" },
  { label: "SUV", to: "/fleet?cat=SUV", testid: "suv" },
];

const DESTINATIONS = [
  { name: "Martha's Vineyard", blurb: "Island ferry from Woods Hole", to: "/boston-to-marthas-vineyard-car-rental", img: "/marketing/marthas-vineyard.webp" },
  { name: "Logan Airport", blurb: "Pickup & drop-off at BOS", to: "/logan-airport-car-rental", img: "/marketing/logan-airport.webp" },
  { name: "Cape Cod", blurb: "Beaches, ~1.5 hrs south", to: "/boston-to-cape-cod-car-rental", img: "https://images.unsplash.com/photo-1509215082347-65c4e2c9161e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxDYXBlJTIwQ29kJTIwbGlnaHRob3VzZSUyMHN1bnNldHxlbnwwfHx8fDE3ODI1MjAyNzB8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format" },
  { name: "Nantucket", blurb: "Island ferry from Hyannis", to: "/boston-to-nantucket-car-rental", img: "/marketing/nantucket.webp" },
  { name: "Salem", blurb: "Witch history, ~30 min north", to: "/boston-to-salem-car-rental", img: "/marketing/salem.webp" },
  { name: "Plymouth", blurb: "Plymouth Rock, ~45 min south", to: "/boston-to-plymouth-car-rental", img: "/marketing/plymouth.webp" },
  { name: "The Berkshires", blurb: "Fall foliage out west", to: "/boston-to-berkshires-car-rental", img: "https://images.unsplash.com/photo-1651353907119-8067027c6dd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxtYXNzYWNodXNldHRzJTIwYmVya3NoaXJlcyUyMGF1dHVtbiUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODIwNjg4MTN8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format" },
  { name: "Gillette Stadium", blurb: "Games, concerts & World Cup 2026", to: "/logan-airport-to-gillette-stadium-car-rental", img: "https://images.unsplash.com/photo-1714213450890-3f465d40ed46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdGFkaXVtJTIwYXJjaGl0ZWN0dXJlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgyOTE1MDQ3fDA&ixlib=rb-4.1.0&q=70&w=1200&auto=format" },
];

export default function Home() {
  const { t } = useTranslation();
  const [cars, setCars] = useState([]);

  useEffect(() => {
    api.get("/cars").then((res) => setCars(res.data.slice(0, 3))).catch(() => {});
  }, []);

  const stats = [
    ["$35", t("home.statDailyRate")],
    ["2 min", t("home.statBooking")],
    [t("home.statAirportValue"), t("home.statAirport")],
  ];
  const features = [
    { icon: BadgeDollarSign, title: t("home.featureRatesTitle"), desc: t("home.featureRatesDesc") },
    { icon: Clock, title: t("home.featureBookingTitle"), desc: t("home.featureBookingDesc") },
    { icon: ShieldCheck, title: t("home.featureReliableTitle"), desc: t("home.featureReliableDesc") },
  ];
  const airportPoints = [t("home.airportPoint1"), t("home.airportPoint2"), t("home.airportPoint3")];

  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        title="Belo Rentals | Boston Car Rental Marketplace & Logan Airport Pickup"
        description="Belo Rentals offers pickup & drop-off at Logan Airport. Book online in 2 minutes."
        path="/"
        keywords="car rental Logan Airport, Boston airport car rental, car rental near Logan Airport Boston, Logan Airport car rental drop off, Belo car rental marketplace, Logan Airport car rental service, cheap car rental Boston, car rental Boston vacation"
      />
      {/* Hero — Turo-style search banner */}
      <section className="relative pt-24 lg:pt-28 pb-2" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative rounded-3xl overflow-hidden min-h-[520px] flex flex-col justify-center px-5 sm:px-8 md:px-12 py-16 shadow-2xl"
            style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-blue-950/85 via-blue-900/55 to-slate-900/70" />
            <div className="relative z-10 w-full max-w-4xl mx-auto text-center">
              <Link to="/fleet?airport=1" data-testid="hero-badge" className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-medium border border-white/20 hover:bg-white/25 transition-colors">
                <Plane size={15} /> Now offering Logan Airport pickup &amp; return <ArrowRight size={14} />
              </Link>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-6 font-bold leading-[1.05]">
                Boston Car Rental <span className="text-blue-300">Marketplace</span>
              </h1>
              <p className="text-white/90 text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
                The affordable local way to rent a car across Greater Boston &amp; Massachusetts
              </p>
              <a href="tel:+19782211451" data-testid="hero-phone" className="inline-flex items-center gap-2 mt-3 text-white font-semibold hover:text-blue-200 transition-colors">
                <Phone size={17} /> (978) 221-1451
              </a>
              <div className="mt-9">
                <HeroSearch />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2.5 mt-7">
                {stats.map(([a, b]) => (
                  <span key={b} className="flex items-center gap-2 bg-white/12 backdrop-blur-md border border-white/15 rounded-full px-4 py-1.5 text-white text-sm">
                    <span className="font-heading font-bold">{a}</span>
                    <span className="text-white/75">{b}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick-filter tabs */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3" data-testid="home-filter-tabs">
            {FILTER_TABS.map((tab) => (
              <Link
                key={tab.testid}
                to={tab.to}
                data-testid={`filter-tab-${tab.testid}`}
                className="rounded-full px-5 py-2.5 border border-slate-200 bg-white text-slate-700 hover:border-blue-600 hover:text-blue-600 hover:shadow-sm transition-all text-sm font-medium"
              >
                {tab.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white px-5 sm:px-8 py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div key={f.title} data-testid={`feature-${i}`} className="bg-slate-50 rounded-2xl p-8 hover:shadow-card transition-shadow">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-600 text-white"><f.icon size={22} /></span>
              <h3 className="font-heading text-xl text-slate-900 mt-5 font-semibold">{f.title}</h3>
              <p className="text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Airport Drop-Off */}
      <section className="bg-white px-5 sm:px-8 py-12 lg:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={AIRPORT} alt="Logan Airport" className="w-full aspect-[4/3] object-cover rounded-3xl shadow-card" />
            <div className="absolute -bottom-5 -right-3 sm:right-6 bg-white rounded-2xl shadow-card-hover px-6 py-4 flex items-center gap-3">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-amber-500 text-white"><Plane size={20} /></span>
              <div>
                <p className="font-heading font-bold text-slate-900 leading-tight">Logan Airport</p>
                <p className="text-slate-500 text-sm">{t("home.airportDropOff")}</p>
              </div>
            </div>
          </div>
          <div>
            <p className="label-caps text-blue-600">{t("home.airportService")}</p>
            <h2 className="font-heading text-3xl lg:text-4xl text-slate-900 mt-3 font-bold tracking-tight">
              {t("home.airportTitle")}
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              {t("home.airportDesc")}
            </p>
            <ul className="mt-6 space-y-3">
              {airportPoints.map((t2) => (
                <li key={t2} className="flex items-start gap-3 text-slate-700">
                  <span className="grid place-items-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 mt-0.5 shrink-0"><Check size={13} /></span>
                  {t2}
                </li>
              ))}
            </ul>

            {/* Airport hosts CTA */}
            <div data-testid="home-airport-hosts" className="mt-7 rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-4">
              <span className="shrink-0 grid place-items-center w-14 h-14 rounded-xl bg-blue-600 text-white"><Plane size={24} /></span>
              <div className="min-w-0">
                <p className="font-heading font-bold text-slate-900">Meet your host at Logan Airport</p>
                <p className="text-slate-500 text-sm mt-0.5">Book a host who offers Logan Airport (BOS) pickup &amp; return, right here on Belo Rentals.</p>
                <Link to="/logan-airport-drop-off" data-testid="home-airport-hosts-link" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm mt-2 hover:gap-2.5 transition-all">
                  See airport hosts <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            <Link to="/logan-airport-drop-off" data-testid="airport-cta" className="inline-flex items-center gap-3 bg-blue-600 text-white px-7 py-3.5 rounded-full font-semibold mt-8 hover:bg-blue-700 transition-all hover:-translate-y-0.5">
              {t("home.airportCta")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured fleet */}
      <section className="bg-slate-50 px-5 sm:px-8 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="label-caps text-blue-600">{t("home.fleetLabel")}</p>
              <h2 className="font-heading text-3xl lg:text-4xl text-slate-900 mt-3 tracking-tight font-bold">{t("home.fleetTitle")}</h2>
            </div>
            <Link to="/fleet" data-testid="view-all-cars" className="hidden sm:flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all">
              {t("home.viewAll")} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cars.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
          </div>
        </div>
      </section>

      {/* Popular destinations */}
      <section className="bg-white px-5 sm:px-8 py-20" data-testid="home-destinations">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <p className="label-caps text-blue-600">Popular trips</p>
            <h2 className="font-heading text-3xl lg:text-5xl text-slate-900 mt-3 tracking-tight font-bold">Where the beauty of Boston takes you.</h2>
            <p className="text-slate-600 mt-4">From coastal escapes to historic towns, explore New England at your own pace.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {DESTINATIONS.map((d, i) => (
              <Link
                key={d.to}
                to={d.to}
                data-testid={`home-destination-${i}`}
                className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-card hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={d.img} alt={`${d.name} car rental`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-4 left-4 grid place-items-center w-10 h-10 rounded-xl bg-blue-600 text-white shadow-md"><MapPin size={18} /></span>
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-slate-900 text-lg">{d.name}</h3>
                  <p className="text-slate-500 text-sm mt-1">{d.blurb}</p>
                  <span className="inline-flex items-center gap-1 text-blue-600 font-semibold text-sm mt-4 group-hover:gap-2 transition-all">
                    Explore <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Areas We Serve */}
      <section className="bg-white px-5 sm:px-8 py-16 border-t border-slate-100" data-testid="home-areas-served">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="label-caps text-blue-600 mb-2">Areas we serve</p>
              <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">Car rental across Greater Boston</h2>
            </div>
            <Link to="/car-rental-locations" className="text-blue-600 font-semibold text-sm hover:underline">All locations &amp; service area →</Link>
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="home-areas-grid">
            {[
              ["/car-rental-everett-ma", "Everett"], ["/car-rental-melrose-ma", "Melrose"], ["/car-rental-medford-ma", "Medford"], ["/car-rental-chelsea-ma", "Chelsea"],
              ["/car-rental-revere-ma", "Revere"], ["/car-rental-somerville-ma", "Somerville"], ["/car-rental-saugus-ma", "Saugus"], ["/car-rental-cambridge-ma", "Cambridge"],
              ["/car-rental-boston-ma", "Boston"], ["/logan-airport-car-rental", "Logan Airport"], ["/car-rental-woburn-ma", "Woburn"], ["/car-rental-lynn-ma", "Lynn"],
              ["/car-rental-watertown-ma", "Watertown"], ["/car-rental-brookline-ma", "Brookline"], ["/car-rental-lexington-ma", "Lexington"], ["/car-rental-burlington-ma", "Burlington"],
            ].map(([to, label]) => (
              <Link key={to} to={to} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 hover:bg-white transition-all">
                <MapPin size={15} className="text-blue-500 shrink-0" /> {label}
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="bg-white px-5 sm:px-8 py-20">
        <div className="max-w-5xl mx-auto rounded-3xl bg-blue-600 px-8 py-16 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-blue-500/40" />
          <div className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-blue-700/40" />
          <div className="relative z-10">
            <CalendarDays size={36} className="text-white/90 mx-auto" />
            <h2 className="font-heading text-3xl lg:text-4xl text-white mt-5 font-bold tracking-tight">{t("home.ctaTitle")}</h2>
            <p className="text-blue-100 mt-4 max-w-lg mx-auto">{t("home.ctaDesc")}</p>
            <Link to="/fleet" data-testid="cta-book-btn" className="inline-flex items-center gap-3 bg-white text-blue-700 px-8 py-4 rounded-full font-semibold mt-8 hover:bg-blue-50 transition-all hover:-translate-y-0.5">
              {t("home.ctaButton")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SEO context — local Boston / Logan Airport intro */}
      <section className="bg-slate-50 px-5 sm:px-8 py-16 border-t border-slate-100" data-testid="home-seo-intro">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">Car rental in Boston &amp; at Logan Airport</h2>
          <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
            <p>
              Belo Rentals is a Boston-based car rental marketplace built for travelers. Whether you're flying into
              Boston Logan International Airport (BOS) or already in the city, you can reserve a clean, well-maintained car
              online in about two minutes — and pick it up near the airport without waiting in a rental-counter line.
              Our fleet ranges from fuel-efficient economy cars for getting around the city to roomy SUVs for family road trips.
            </p>
            <p>
              From our location minutes from Logan, the whole region is within easy reach: the beaches of Cape Cod, the
              islands of Martha's Vineyard and Nantucket, historic Salem and Plymouth, Gillette Stadium in Foxborough, and
              the fall foliage of the Berkshires. Rates start at $35/day with weekly pricing for longer stays, and Logan
              Airport pickup &amp; return is available. Book direct
              with a local team you can actually reach.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
